<script setup lang="ts">
import { computed, watch } from 'vue';
import { useForm } from 'vee-validate';
import VModal from '@shared/component/VModal/VModal.vue';
import FieldRowGroup from '@shared/form/FieldRowV2/FieldRowGroup.vue';
import { RelatieRekeningField } from '@relatie-lib/relatieGegevens/relatiegegevensForm/fields';
import IngangsdatumField from './fields/IngangsdatumField.vue';
import HoofdvervaldatumField from './fields/HoofdvervaldatumField.vue';
import IncassomethodeField from './fields/IncassomethodeField.vue';
import IncassotermijnField from './fields/IncassotermijnField.vue';
import { usePakketpolisStore } from '../pakketpolisStore';

const props = defineProps<{
  pakketpolisnummer: string;
}>();

interface PakketpolisForm {
  ingangsdatum: Date;
  hoofdvervaldatum: Date;
  incassomethode: string;
  incassotermijn: string;
  rekening: {
    iban?: string;
    bic?: string;
  };
}

const isOpen = defineModel<boolean>('isOpen');

const store = usePakketpolisStore();
const pakketpolis = computed(() => store.getPakketpolis(props.pakketpolisnummer));

const { errors, resetForm, values } = useForm<PakketpolisForm>({
  initialValues: {
    ingangsdatum: pakketpolis.value?.ingangsdatum ?? new Date(),
    hoofdvervaldatum: pakketpolis.value?.hoofdvervaldatum ?? new Date(),
    incassomethode: pakketpolis.value?.incassomethode ?? '',
    incassotermijn: pakketpolis.value?.incassotermijn ?? '',
  },
});

watch(
  pakketpolis,
  (newValue) => {
    if (newValue) {
      resetForm({
        values: {
          ingangsdatum: newValue.ingangsdatum ?? new Date(),
          hoofdvervaldatum: newValue.hoofdvervaldatum ?? new Date(),
          incassomethode: newValue.incassomethode ?? '',
          incassotermijn: newValue.incassotermijn ?? '',
        },
      });
    }
  },
  { immediate: true },
);

const incassoTooltip = 'Wijzigingen omtrent incasso zullen ingaan per eerstvolgende betaaltermijn.';

const isFormValid = computed(() => Object.keys(errors.value).length === 0);

const isIbanRequired = computed(() => {
  const methode = pakketpolis.value?.mutatieActies.incasso.methode.opties.find(
    (o) => o.waarde === values.incassomethode,
  );

  return methode?.vraagIbanUit ?? false;
});

function close() {
  isOpen.value = false;
}

function save() {
  if (!isFormValid.value) {
    return;
  }

  if (pakketpolis.value?.mutatieActies.ingangsdatum.isMogelijk) {
    store.setIngangsdatum(props.pakketpolisnummer, values.ingangsdatum);
  }

  if (pakketpolis.value?.mutatieActies.hoofdvervaldatum.isMogelijk) {
    store.setHoofdvervaldatum(props.pakketpolisnummer, values.hoofdvervaldatum);
  }

  if (pakketpolis.value?.mutatieActies.incasso.methode.isMogelijk) {
    store.setIncassomethode(props.pakketpolisnummer, values.incassomethode);

    if (isIbanRequired.value && !!values.rekening?.iban) {
      store.setRekening(props.pakketpolisnummer, {
        ...values.rekening,
        iban: values.rekening.iban,
      });
    }
  }

  if (pakketpolis.value?.mutatieActies.incasso.termijn.isMogelijk) {
    store.setIncassotermijn(props.pakketpolisnummer, values.incassotermijn);
  }

  close();
}

const incassomethodeOptions = computed(
  () =>
    pakketpolis.value?.mutatieActies.incasso.methode.opties.map((im) => ({
      label: im.label,
      value: im.waarde,
      disabled: !im.isMogelijk,
    })) ?? [],
);

const incassotermijnOptions = computed(() => {
  const methode = pakketpolis.value?.mutatieActies.incasso.methode.opties.find(
    (o) => o.waarde === values.incassomethode,
  );

  const opties = methode?.opties.map((it) => ({
    label: it.label,
    value: it.waarde,
    disabled: !it.isMogelijk,
    description: it.melding ?? undefined,
  }));

  opties?.sort((a, b) => a.value.localeCompare(b.value));

  return opties ?? [];
});

watch(
  () => incassotermijnOptions.value,
  (options) => {
    const current = options.find((o) => o.value === values.incassotermijn);
    if (current && !current.disabled) {
      return;
    }

    const incassotermijn = options.filter((o) => !o.disabled)[0]?.value;
    resetForm({
      values: {
        ...values,
        incassotermijn,
      },
    });
  },
);
</script>

<template>
  <VModal
    v-if="pakketpolis"
    :is-active="isOpen"
    @close-modal="close()"
    @confirm-action="save()"
    title="Muteren"
    confirm-button-text="Mutatie opslaan"
    :disabled="Object.keys(errors).length > 0"
    overflow
    invert-header
  >
    <div class="min-h-48">
      <FieldRowGroup :gap="2">
        <IngangsdatumField
          is-required
          :min-date="pakketpolis.minimaleIngangsdatum"
          :max-date="pakketpolis.maximaleIngangsdatum"
          :disabled="!pakketpolis.mutatieActies.ingangsdatum.isMogelijk"
          :tooltip="pakketpolis.mutatieActies.ingangsdatum.melding ?? undefined"
        />
        <HoofdvervaldatumField
          is-required
          :min-date="pakketpolis.minimaleHoofdvervaldatum"
          :max-date="pakketpolis.maximaleHoofdvervaldatum"
          :disabled="!pakketpolis.mutatieActies.hoofdvervaldatum.isMogelijk"
          :tooltip="pakketpolis.mutatieActies.hoofdvervaldatum.melding ?? undefined"
        />
        <IncassomethodeField
          :options="incassomethodeOptions"
          :disabled="!pakketpolis.mutatieActies.incasso.methode.isMogelijk"
          :tooltip="incassoTooltip"
        />
        <RelatieRekeningField
          v-if="isIbanRequired"
          id="incasso-iban"
          :is-required="isIbanRequired"
          label-on-own-row
        />
        <IncassotermijnField
          :options="incassotermijnOptions"
          :disabled="!pakketpolis.mutatieActies.incasso.termijn.isMogelijk"
          :tooltip="incassoTooltip"
        />
      </FieldRowGroup>
    </div>
  </VModal>
</template>
